import { ButtonHeading} from "components";
import Tab from "components/common/Tab";
import { BankSelectModal, MakePaymentModal } from "modals";
import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import {
  ApprovedTab,
  CompletedTab,
  HoldTab,
  PendingTab,
  RejectedTab,
  PaymentFromCBTab,
  UnderApprove,
  TransactionInitiate,
  TransactionFailed
} from "components/PaymentDetailsTabs";
import { UserContext } from "context";
import { paymentCountApi } from "_services";
import toast from "react-hot-toast";
import StorageService from "utils/StorageServices/StorageServices";
import MastersFilterModal from "modals/MastersFilterModal";
import useDocumentTitle from "hooks/useDocumentTitle";
import { API, AppConfig } from "lib";
import './paymentApproval.css'
import { useLocation } from "react-router-dom";

const PaymentApproval = () => {
  const { masterData, allMenu } = useContext(UserContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromQuery = parseInt(queryParams.get('tab')) || 1;
  
  const [showMakePayment, setShowMakePayment] = useState(false);
  const [activeTab, setActiveTab] = useState(tabFromQuery);
  const [newTabList, setNewTabList] = useState([]);
  
  // Use single initial filter object - define once outside to prevent recreation
  const initialFilterRef = useRef({
    branch_id: "",
    start_date: new Date(),
    end_date: "",
    expense_category_id: "",
    search: "",
    limit: 10,
    offset: 1,
    company_id: StorageService.getCompanyId(),
    param_user_id: "",
    user_id: "",
    user_name: "",
    branch_name: ""
  });
  
  const [filterInput, setFilterInput] = useState(initialFilterRef.current);
  const [filter, setFilter] = useState(initialFilterRef.current);
  
  const [isTableToggle, setIsTableToggle] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [userlist, setUserList] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [list, setList] = useState({
    branch: [],
    user: [],
    expense: [],
  });
  const [bankModalShow, setBankModalShow] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    bank: ""
  });

  const [loadingExcel, setLoadingExcel] = useState(false);
  const [listOfPayment, setListOfPayment] = useState([]);
  const [openAccounts, setOpenAccounts] = useState(false);
  const [state, setState] = useState({
    filterCount: 0,
  });

  // Use refs to track renders and prevent duplicate calls
  const isFirstRender = useRef(true);
  const hasCalledApi = useRef(false);
  const filterRef = useRef(filter); // Store latest filter in ref
  const isToken = localStorage.getItem("token");

  useDocumentTitle("Payment Approval | T360");

  // Keep filterRef updated with latest filter value
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  // Debounce effect - skip on first render
  useEffect(() => {
    if (isFirstRender.current) {
      return; // Don't set up timeout on first render
    }

    const handler = setTimeout(() => {
      setFilter(filterInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [filterInput]);

  // Update activeTab only if it changes
  useEffect(() => {
    if (tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  useEffect(() => {
    setList((prevList) => ({
      ...prevList,
      branch: branchList,
      user: userlist || [],
      expense: expenseList,
    }));
  }, [branchList, expenseList]);

  // Call API when filter changes - with duplicate prevention
  useEffect(() => {
    const getPaymentCount = async () => {
      console.log('🔵 Automatic API call on filter change', { filter });
      
      // Prevent duplicate call on initial mount
      if (isFirstRender.current) {
        if (hasCalledApi.current) {
          console.log('❌ Prevented duplicate call on mount');
          return;
        }
        hasCalledApi.current = true;
        isFirstRender.current = false;
      }

      try {
        const res = await paymentCountApi({...filter,firm_id: StorageService?.getFirmId() ?? "",});

        if (res?.responseCode === 200) {
          setNewTabList(res?.data?.approval_count?.map((i) => {
            return {
              title: i?.label,
              count: i?.value,
              id: i?.approval_status,
              amount: i?.amount || 0,
            };
          }));
        } else {
          toast.error(res?.responseMessage);
        }
      } catch (error) {
        toast.error(error);
      }
    };

    getPaymentCount();
  }, [filter]);

  // Separate function for manual refresh (used by child components)
  // This will NOT re-create on filter changes, but will use latest filter value
  const refreshPaymentCount = useCallback(async () => {
    console.log("🟢 Manual refresh called - refreshPaymentCount");
    try {
      const res = await paymentCountApi({...filterRef.current,firm_id: StorageService?.getFirmId() ?? "",}); // Use ref to get latest filter

      if (res?.responseCode === 200) {
        setNewTabList(res?.data?.approval_count?.map((i) => {
          return {
            title: i?.label,
            count: i?.value,
            id: i?.approval_status,
            amount: i?.amount || 0,
          };
        }));
      } else {
        toast.error(res?.responseMessage);
      }
    } catch (error) {
      toast.error(error);
    }
  }, []); // Empty dependency - function never changes

  const handleDownloadExcel = async () => {
    setLoadingExcel(true);
    try {
      const payload = {
        company_id: StorageService?.getCompanyId(),
        bank_id: bankDetails?.bank?._id,
        approval_status: activeTab
      };
      const response = await fetch(`${AppConfig.api_baseurl}${API.PAYMENT_EXCEL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Token': `${isToken}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Bank-Format.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setLoadingExcel(false);
      setBankModalShow(false);
      setBankDetails({
        bank: ""
      });
    } catch (error) {
      setLoadingExcel(false);
      toast.error("Failed to download excel");
    }
  };

  const callBackTab = (data) => {
    setActiveTab(data);
  };

  const tabProps = {
    tabList: newTabList,
    callBackTab: callBackTab,
    activeTab: activeTab,
    showAmount: true
  };

  const callbackLit = (data) => {
    setListOfPayment(data);
  };

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const clearFilter = useCallback(() => {
    const clearedFilter = {
      branch_id: "",
      start_date: new Date(),
      end_date: "",
      expense_category_id: "",
      search: "",
      limit: 10,
      offset: 1,
      company_id: StorageService.getCompanyId(),
      param_user_id: "",
      user_id: "",
      user_name: "",
      branch_name: ""
    };
    setFilterInput(clearedFilter);
    setFilter(clearedFilter);
    updateState({ filterCount: 0 });
  }, [updateState]);

  // Calculate filter count
  useEffect(() => {
    const filteredValues = Object.entries(filterInput)
      .filter(([key, val]) =>
        ["branch_id", "user_id"].includes(key) &&
        val !== "" &&
        val !== null &&
        val !== undefined &&
        val !== 0
      )
      .map(([_, val]) => val);

    updateState({
      filterCount: filteredValues.length,
    });
  }, [filterInput, updateState]);

  const pendingProps = {
    activeTab: activeTab,
    userlist: [],
    filter: filter,
    filterInput: filterInput,
    setFilterInput: setFilterInput,
    getPaymentCount: refreshPaymentCount,
    callbackLit: callbackLit,
    setOpenAccounts: setOpenAccounts
  };

  let firstMakePayment = {
    isOpen: showMakePayment,
    closeModal: (e) => setShowMakePayment(false),
    headerPart: false,
    footerPart: true,
    modalMainClass: "_dashCommon_Modal _smModal",
    modalTitle: "Make Payment",
    submitTitle: "Add Freight",
    cancelButtonTitle: "Send for Approval",
  };

  let bankModalProps = {
    isOpen: bankModalShow,
    closeModal: (e) => {
      setBankDetails({
        bank: ""
      });
      setBankModalShow(false);
    },
    modalMainClass: "_dashCommon_Modal _smModal",
    modalTitle: "Bank List",
    submitTitle: "Export",
    // onSubmit: userlist?.length > 0 ? handleDownloadExcel : () => { },
    onSubmit: handleDownloadExcel ,

    setBankDetails: setBankDetails,
    bankDetails: bankDetails,
    isLoadding: loadingExcel
  };

  return (
    <>
      <ButtonHeading
        title="Payment Approval"
        buttonTitle="Payment Approval"
        showSearchFilter={{
          search: true,
          filterOption: true,
          filterCount: true,
        }}
        onClickFilterModal={(e) => setShowFilterModal(!showFilterModal)}
        filter={filterInput}
        setFilter={setFilterInput}
        isDebouncedSearch={false}
        filterCount={state.filterCount}
        clearFilter={clearFilter}
      />
      <div className="addPr_flBx __anvNw_pay_flBx">
        <Tab 
          {...tabProps} 
          invoiceBillRaiseClass="__anvNw_payApp" 
          isActiveButton={(activeTab == 2 || activeTab == 3) ? true : false} 
          tabButtonName="Export" 
          rightButton={() => listOfPayment?.length ? setBankModalShow(true) : ""} 
        />

        {activeTab === 1 && <PendingTab {...pendingProps} />}
        {activeTab === 3 && <ApprovedTab {...pendingProps} />}
        {activeTab === 2 && <HoldTab {...pendingProps} />}
        {activeTab === 4 && <RejectedTab {...pendingProps} />}
        {activeTab === 5 && <CompletedTab {...pendingProps} />}
        {activeTab === 6 && <PaymentFromCBTab {...pendingProps} />}
        {activeTab === 7 && <UnderApprove {...pendingProps} />}
        {activeTab == 8 && <TransactionInitiate {...pendingProps} />}
        {activeTab == 9 && <TransactionFailed {...pendingProps} />}
      </div>

      <div
        className={`offcanvas offcanvas-end common_offCanvas ${showFilterModal ? "show" : ""}`}
        tabIndex={-1}
        style={{ visibility: showFilterModal ? "visible" : "hidden" }}
      >
        {showFilterModal && (
          <MastersFilterModal
            isOpen={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            filter={filterInput}
            setFilter={setFilterInput}
            list={list}
            isShow={{
              search:false,
              datePicker: true,
              allUser: true,
              allBranch: true,
              expenseCatagory: true,
            }}
            name="payment"
            setFilterCount={(count) => updateState({ filterCount: count })}
          />
        )}
      </div>

      <div className={showFilterModal ? 'offBack_drop' : ''}></div>

      {showMakePayment && <MakePaymentModal {...firstMakePayment} />}
      {bankModalShow && <BankSelectModal {...bankModalProps} />}
    </>
  );
};

export default PaymentApproval;